/**
 * MessageActionMenu Component
 * 
 * Combined menu for message actions: reactions and delete.
 * Features:
 * - Shows the quick emoji reactions (❤️ 👍 👎 😂 😮 😢 🎉) plus, at the far right,
 *   a greyed-out face with a "+" badge that swaps the row for the full
 *   searchable picker (any emoji, 2026-09-09)
 * - Delete button for own messages
 * - Positioned above or below the message
 * - Auto-adjusts position to stay within viewport
 * - Closes when clicking outside
 */

import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Reply, Copy, Pencil } from 'lucide-react';
import { REACTION_EMOJIS } from '@/lib/firestore';
import { LazyEmojiPicker } from '@/components/shared/EmojiPickerButton';
import { MoreReactionsButton } from '@/components/shared/ReactionPicker';

export default function MessageActionMenu({ 
  onEmojiSelect, 
  onDelete,
  onEdit,
  onReply,
  onCopy,
  onClose, 
  position = 'above',
  currentUserReactions = [],
  isOwnMessage = false,
  showDelete = true,
  showEdit = false,
  anchorRef,
  anchorPoint = null // {x, y} in viewport px: position near the pointer (right-click) instead of the bubble
}) {
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({ position: 'fixed', opacity: 0, pointerEvents: 'none' });
  const [isPositioned, setIsPositioned] = useState(false);
  const [showAllEmoji, setShowAllEmoji] = useState(false); // full picker replaces the pill

  // Calculate fixed position relative to anchor element, rendered via portal
  useLayoutEffect(() => {
    if (!menuRef.current || !anchorRef?.current) return;
    
    const menu = menuRef.current;
    const bubbleRect = anchorRef.current.getBoundingClientRect();
    // A right-click on a tall bubble (long text, embed) should open next to
    // the pointer, not off-screen above/below a bubble taller than the window.
    const anchorRect = anchorPoint
      ? { top: anchorPoint.y, bottom: anchorPoint.y, left: bubbleRect.left, width: bubbleRect.width, height: 0 }
      : bubbleRect;
    const menuRect = menu.getBoundingClientRect();
    const padding = 12;
    const gap = 8;
    
    // Determine vertical position - try preferred position first, flip if needed
    let top;
    if (position === 'above') {
      top = anchorRect.top - menuRect.height - gap;
      if (top < padding) {
        // Not enough room above, flip to below
        top = anchorRect.bottom + gap;
      }
    } else {
      top = anchorRect.bottom + gap;
      if (top + menuRect.height > window.innerHeight - padding) {
        top = anchorRect.top - menuRect.height - gap;
      }
    }
    
    // Whatever the flip decided, keep the menu inside the viewport.
    top = Math.max(padding, Math.min(top, window.innerHeight - menuRect.height - padding));

    // Horizontal: center on anchor, clamp to viewport
    let left = anchorRect.left + anchorRect.width / 2 - menuRect.width / 2;
    if (left + menuRect.width > window.innerWidth - padding) {
      left = window.innerWidth - padding - menuRect.width;
    }
    if (left < padding) {
      left = padding;
    }
    
    setMenuStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 9999,
      pointerEvents: 'auto',
    });
    
    setIsPositioned(true);
  }, [anchorRef, anchorPoint, position, showAllEmoji]); // re-measure when the pill becomes the picker

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleEmojiClick = (emoji) => {
    onEmojiSelect(emoji);
    onClose();
  };

  // Handle touch on emoji - more reliable than click on mobile
  const handleEmojiTouch = (emoji, e) => {
    e.stopPropagation();
    e.preventDefault();
    onEmojiSelect(emoji);
    onClose();
  };

  const handleDeleteClick = () => {
    onDelete();
    onClose();
  };

  // Handle touch on delete - more reliable than click on mobile
  const handleDeleteTouch = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete();
    onClose();
  };

  if (showAllEmoji) {
    return createPortal(
      <div
        ref={menuRef}
        data-ui="actpill"
        className={`bg-card rounded-2xl shadow-lg border border-slate-200 dark:border-white/10 p-1 overflow-hidden ${isPositioned ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} transition-all duration-150 ease-out`}
        style={menuStyle}
        data-testid="message-action-menu"
      >
        <LazyEmojiPicker autoFocusSearch onEmojiSelect={handleEmojiClick} />
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      ref={menuRef}
      data-ui="actpill"
      className={`
        flex items-center
        bg-card rounded-full shadow-lg border border-slate-200 dark:border-white/10 
        px-1 sm:px-2 py-1
        ${isPositioned ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        transition-all duration-150 ease-out
      `}
      style={menuStyle}
      data-testid="message-action-menu"
    >
      {/* Quick reactions, plus any custom emoji this user already put on the message so it can be removed in one tap */}
      {[...REACTION_EMOJIS, ...currentUserReactions.filter((e) => !REACTION_EMOJIS.includes(e))].map((emoji) => {
        const isSelected = currentUserReactions.includes(emoji);
        
        return (
          <button
            key={emoji}
            onClick={() => handleEmojiClick(emoji)}
            onTouchEnd={(e) => handleEmojiTouch(emoji, e)}
            data-ui="rx-btn"
            data-on={isSelected ? 'true' : undefined}
            className={`
              w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center 
              rounded-full text-lg sm:text-lg
              transition-all duration-150
              hover:scale-125 hover:bg-slate-100 dark:hover:bg-white/10
              active:scale-100 active:bg-slate-200
              touch-action-manipulation
              ${isSelected ? 'bg-blue-100 dark:bg-blue-900/50 ring-2 ring-blue-400' : ''}
            `}
            style={{ touchAction: 'manipulation' }}
            title={isSelected ? 'Remove reaction' : 'Add reaction'}
            data-testid={`reaction-${emoji}`}
          >
            {emoji}
          </button>
        );
      })}

      {/* Any other emoji: greyed face with a "+" badge, far right of the row */}
      <MoreReactionsButton size="dm" onClick={() => setShowAllEmoji(true)} />

      {/* Reply button */}
      {onReply && (
        <>
          <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145] mx-0.5" />
          <button
            onClick={() => { onReply(); onClose(); }}
            onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onReply(); onClose(); }}
            data-ui="ibtn-sm"
            className="w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-slate-400 dark:text-slate-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/15 active:bg-blue-100 transition-all duration-150"
            style={{ touchAction: 'manipulation' }}
            title="Reply"
            data-testid="reply-message-btn"
          >
            <Reply className="w-4 h-4" />
          </button>
        </>
      )}

      {/* Copy button */}
      {onCopy && (
        <>
          <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145] mx-0.5" />
          <button
            onClick={() => { onCopy(); onClose(); }}
            onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onCopy(); onClose(); }}
            data-ui="ibtn-sm"
            className="w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 active:bg-slate-100 transition-all duration-150"
            style={{ touchAction: 'manipulation' }}
            title="Copy message"
            data-testid="copy-message-btn"
          >
            <Copy className="w-4 h-4" />
          </button>
        </>
      )}

      {/* Edit button - only for own text messages within time window */}
      {showEdit && isOwnMessage && (
        <>
          <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145] mx-0.5" />
          <button
            onClick={() => { onEdit(); onClose(); }}
            onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onEdit(); onClose(); }}
            data-ui="ibtn-sm"
            className="w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-slate-400 dark:text-slate-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/15 active:bg-blue-100 transition-all duration-150"
            style={{ touchAction: 'manipulation' }}
            title="Edit message"
            data-testid="edit-message-btn"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </>
      )}

      {/* Divider and Delete button - only for own messages */}
      {showDelete && isOwnMessage && (
        <>
          <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145] mx-0.5" />
          <button
            onClick={handleDeleteClick}
            onTouchEnd={handleDeleteTouch}
            data-ui="ibtn-sm"
            data-tone="bad"
            className="w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-slate-400 dark:text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/15 active:bg-red-100 transition-all duration-150"
            style={{ touchAction: 'manipulation' }}
            title="Delete message"
            data-testid="delete-message-btn"
          >
            <Trash2 className="w-4 h-4 sm:w-4 sm:h-4" />
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
